import {
  defineComponent,
  ref,
  provide,
  Ref,
  onMounted
} from '@vue/composition-api';
import { useConfig } from './useConfig';

interface UseConfiguratorOptions {
  disablePropsBinding?: boolean;
}

/**
 * Creates a new configurator instance.
 *
 * @param configuration object
 * @param className     string|undefined
 *
 * @return VueProxy
 */
export const useConfigurator = (
  configuration: object,
  className?: string,
  options?: UseConfiguratorOptions
) => {
  const components = {};
  for (const [key, value] of Object.entries(configuration)) {
    components[key] = value.component;
  }

  return defineComponent({
    components: {
      ...components
    },
    props: {
      component: {
        required: true,
        type: String
      },
      configId: {
        required: false,
        type: String
      },
      configObject: {
        required: false,
        type: Object
      }
    },
    setup(props: any) {
      const { component, configId, configObject } = props;
      const componentProps: Ref<Record<string, unknown> | null> = ref(null);

      onMounted(async () => {
        if (configuration && configuration[component]) {
          const defaultConfig = configuration[component].config
            ? await configuration[component].config()
            : false;

          const { config } = useConfig(
            configId,
            configObject,
            defaultConfig.default
          );

          componentProps.value = config;
        } else {
          componentProps.value = {};
        }
      });

      let bindProps: unknown = componentProps;

      if (options?.disablePropsBinding) {
        bindProps = null;
      }

      provide('$config', componentProps);

      return {
        componentProps,
        className,
        bindProps
      };
    },
    template: `
      <component
        v-if="componentProps !== null"
        v-bind="bindProps"
        :is="component"
        :class="className"
      />
    `
  });
};
